import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, MessageSquare, BarChart3, FileText, Users, DollarSign, Percent, User, Edit } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import PositListCard from '@/components/posit/PositListCard';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import OnboardingFlow from '@/components/onboarding/OnboardingFlow';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

/**
 * DASHBOARD - Main Overview Page
 * 
 * This is the central hub where users see all their Posit activity and metrics.
 * 
 * KEY FEATURES:
 * 1. Posit Statistics: Total count, active (public) count
 * 2. Chat Analytics: Session count and engagement metrics
 * 3. Revenue Tracking: Earnings from monetized Posits
 * 4. Subscriber Management: People subscribed to user's Posits
 * 5. Affiliate Earnings: Commission tracking for Plus tier users
 * 
 * DATA SOURCES:
 * - posits table: User's created knowledge bases
 * - chat_history table: Conversation analytics
 * - saved_posits table: Subscriber count (simplified approach)
 * - Stripe: Revenue and payment data
 * - Affiliate system: Commission tracking for referrals
 * 
 * SIDEBAR INTEGRATION:
   * Uses DashboardLayout which provides the unified sidebar navigation
  * and consistent header patterns across all dashboard pages.
  */

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  // Get user premium status (same logic as other components)
  const { data: userProfile } = useQuery({
    queryKey: ["user-profile-dashboard", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("users")
        .select("subscription_type, trial_end_date")
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      const isTrialValid = data?.trial_end_date ? new Date(data.trial_end_date) > new Date() : false;
      const isPremiumOrTrial = data?.subscription_type === 'posit_plus' || 
                              (data?.subscription_type === 'trial' && isTrialValid);
      
      return {
        ...data,
        isPremium: isPremiumOrTrial
      };
    },
    enabled: !!user?.id
  });
  
  // DASHBOARD STATISTICS QUERY
  // Aggregates data from multiple sources to provide comprehensive overview
  const { data: dashboardStats, isLoading } = useQuery({
    queryKey: ['dashboard-stats', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Get user's posits with monetization info
      const { data: posits, error: positsError } = await supabase
        .from('posits')
        .select('id, privacy, is_monetized, subscription_price')
        .eq('user_id', user.id);

      if (positsError) throw positsError;

      const positIds = posits?.map(p => p.id) || [];

      // Get chat sessions count (messages sent to your posits)
      const { count: chatSessions } = await supabase
        .from('chat_history')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds);

      // Get active subscribers across all of the user's posits
      const { count: activeSubscribers } = await supabase
        .from('posit_subscriptions')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds)
        .eq('status', 'active');

      // Get subscriber count (simplified)
      const { count: subscribers } = await supabase
        .from('saved_posits')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds);

      // Get affiliate stats if user has Posit+ subscription
      let affiliateEarnings = 0;
      const { data: userProfile } = await supabase
        .from('users')
        .select('subscription_type, handle')
        .eq('id', user.id)
        .single();

      if (userProfile?.subscription_type === 'posit_plus') {
        try {
          const { data: affiliateData } = await supabase.functions.invoke('affiliate-management/stats', {
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
            }
          });
          
          affiliateEarnings = affiliateData?.data?.totalEarned || 0;
        } catch (error) {
        }
      }

      return {
        totalPosits: posits?.length || 0,
        activePosits: posits?.filter(p => p.privacy === 'public')?.length || 0,
        chatSessions: chatSessions || 0,
        monthlySubscribers: activeSubscribers || 0,
        subscribers: subscribers || 0,
        affiliateEarnings,
        hasAffiliateAccess: userProfile?.subscription_type === 'posit_plus',
        userHandle: userProfile?.handle
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch user's posits for the posits section
  const { data: userPosits, isLoading: isLoadingPosits } = useQuery({
    queryKey: ['dashboard-posits', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('posits')
        .select(`
          id,
          title,
          summary_light,
          posit_description,
          public_description,
          privacy,
          is_monetized,
          subscription_price,
          created_at,
          updated_at
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  const handlePositCreated = (positId: string) => {
    toast({
      title: "Posit created!",
      description: "You can now start adding content to your Posit.",
    });
    navigate(`/update-posit/${positId}`);
  };

  const stats = dashboardStats || {
    totalPosits: 0,
    activePosits: 0,
    chatSessions: 0,
    monthlySubscribers: 0,
    subscribers: 0,
    affiliateEarnings: 0,
    hasAffiliateAccess: false,
    userHandle: null
  };

  return (
    <DashboardLayout
      title="Dashboard"
    >
      <div className="space-y-4 lg:space-y-6 w-full">
        
              
        {/* Posits Section - First */}
        <div className="bg-card rounded-lg shadow py-6">
          {isLoadingPosits ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : userPosits && userPosits.length > 0 ? (
            <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(340px,1fr))]">
              {userPosits.slice(0, 3).map((posit) => {
                const isPaid = posit.is_monetized && posit.subscription_price > 0;
                const price = isPaid ? `$${posit.subscription_price}/m` : null;
                return (
                  <PositListCard
                    key={posit.id}
                    posit={posit}
                    showEditButton={true}
                    showCreatorButton={false}
                    showShareButton={true}
                    showWikiButton={false}
                    isPremium={userProfile?.isPremium || false}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-sm mb-4">
                No Posits yet. Create your first Posit to get started!
              </p>
              <Button 
                onClick={() => navigate('/dashboard/create')}
                size="sm"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Posit
              </Button>
            </div>
          )}
        </div>

        {/* Action Cards - Quick Actions and Key Metrics - Second */}
        <div className="grid gap-4 lg:gap-6 lg:grid-cols-2 flex-1 w-full">
          <Card className="flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 space-y-3">
              <Button 
                onClick={() => navigate('/dashboard/create')} 
                className="w-full justify-start"
                size="default"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Posit
              </Button>
              {stats.userHandle && (
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/p/${stats.userHandle}`)}
                  className="w-full justify-start"
                  size="default"
                >
                  <User className="mr-2 h-4 w-4" />
                  View Profile
                </Button>
              )}
              <Button 
                variant="outline"
                onClick={() => navigate('/dashboard/analytics')}
                className="w-full justify-start"
                size="default"
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                View Analytics
              </Button>
              {stats.hasAffiliateAccess && (
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard/affiliate')}
                  className="w-full justify-start"
                  size="default"
                >
                  <Percent className="mr-2 h-4 w-4" />
                  Affiliate Program
                </Button>
              )}
              <Button 
                variant="outline"
                onClick={() => navigate('/dashboard/posits')}
                className="w-full justify-start"
                size="default"
              >
                <FileText className="mr-2 h-4 w-4" />
                Manage Posits
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/public-posits')}
                className="w-full justify-start"
                size="default"
              >
                <Users className="mr-2 h-4 w-4" />
                Explore Posits
              </Button>
            </CardContent>
          </Card>
          
          <Card className="flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Key Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Posits Active</p>
                      <p className="text-2xl font-bold">{stats.activePosits}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">of {stats.totalPosits}</p>
                      <p className="text-sm text-muted-foreground">total</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Total Chats</p>
                      <p className="text-2xl font-bold">{stats.chatSessions}</p>
                    </div>
                    <div className="text-right">
                      <MessageSquare className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Monthly Subscribers</p>
                      <p className="text-2xl font-bold">{stats.monthlySubscribers}</p>
                    </div>
                    <div className="text-right">
                      <Users className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Affiliate Earnings</p>
                      <p className="text-2xl font-bold">${stats.affiliateEarnings.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <Percent className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

      </div>
    </DashboardLayout>
  );
};

export default Dashboard; // Build test Sun Aug 10 18:08:57 PDT 2025
